import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { CommandColumnService, GridComponent, InfiniteScrollService, PageService, SelectionService, SortService, ToolbarService } from '@syncfusion/ej2-angular-grids';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { CustomAdaptor, gridActionBeginForFiltering, gridActionsForFiltering, handleClearFilter, updateFilterIcon, applyInfiniteGridTotalCount, bindInfiniteScrollPrefetch } from 'projects/service/src/app/shared/utils/grid-functions';
import { environment } from 'projects/service/src/environments/environment';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { UserRoles } from 'projects/service/src/app/shared/models/app.model';

const BASE_URL = environment.apiUrl;

@Component({
    selector: 'app-config-advanced-search',
    templateUrl: './config-advanced-search.component.html',
    styleUrls: ['./config-advanced-search.component.scss'],
    providers: [
        ToolbarService,
        SortService,
        PageService,
        InfiniteScrollService,
        SelectionService, CommandColumnService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ConfigAdvancedSearchComponent implements OnInit {
  isExpanded: boolean = true;
  validationApplied: boolean = false;
  data: any;
  mainGrid: any;
  filterSettings: any;
  query: Query;
  selectedRowData: any;
  public infiniteScrollSettings: any = { enableCache: false, initialBlocks: 3, maxBlocks: 10 };
  totalRecords: number = 0;
  gridHeight: number = window.innerHeight - 350;
  @Input() gridType: any = 'config';

  USER;

  @Input() serviceDepartment;
  @Input() company;

  @Output() onClose = new EventEmitter();
  @ViewChild('columnChooser') columnChooser;
  @ViewChild('serviceOrderGrid') serviceOrderGrid;

  userRoles: any;

  city = ''; state = ''; zipCode = ''; customerName = ''; address = '';
  constructor(private commonService: CommonService) {
    this.USER = commonService.USER;
  }

  ngOnInit(): void {
    this.USER = this.commonService.USER || this.commonService.getUSER();
    this.company = this.company || this.USER?.company || localStorage.getItem('company');
    this.serviceDepartment = this.serviceDepartment || this.USER?.serviceDepartment;

    this.userRoles = UserRoles;
    if (this.USER?.role !== UserRoles.iscManager && this.USER?.role !== UserRoles.isc && this.USER?.role !== UserRoles.IscSupervisor) {
      this.filterSettings = { type: 'Menu' };
      this.getCustomers(null, true);
    } else {
      this.data = [];
    }
  }

  validate() {
    this.validationApplied = true;
    return this.customerName && this.city && this.state;
  }

  setQuery(isIsc) {
    const company = this.company || this.commonService.USER?.company || this.USER?.company || localStorage.getItem('company') || '';
    const serviceDept = this.serviceDepartment || this.commonService.USER?.serviceDepartment || this.USER?.serviceDepartment || '';
    
    let query = new Query().addParams('company', company);
    if (this.gridType !== 'config') {
      query.addParams('$orderBy', 'tax');
    }
    if (isIsc) {
      query.addParams('isIsc', 'true');
      query.addParams('serviceDepartment', serviceDept);
      this.city && query.addParams('city', this.city);
      this.state && query.addParams('stateProvince', this.state);
      this.zipCode && query.addParams('zipCode', this.zipCode);
      this.customerName && query.addParams('bpDescription', this.customerName);
      this.address && query.addParams('address', this.address);
    }
    return query;
  }

  getCustomers(event, onInit?: boolean) {
    if (this.validate() || onInit) {
      const isIsc = this.USER?.role === UserRoles.iscManager || this.USER?.role === UserRoles.IscSupervisor || this.USER?.role === UserRoles.isc;
      const getCustomerApi = isIsc ? 'SoldToBP/GetISCCustomerSearchView' : 'SoldToBP/GetCustomerSearchView'
      const api = this.gridType === 'config' ? getCustomerApi : 'SoldToBP/GetSoldToBP';

      this.data = new DataManager({
        url: `${BASE_URL}${api}`,
        adaptor: new CustomAdaptor(),
      });
      this.query = this.setQuery(isIsc);;
    }
  }

  ngAfterViewInit(): void {
    this.setGrid();
  }

  setGrid() {
    const columns = this.gridType === 'config' ? [
      { field: 'soldToBP', headerText: 'ID', width: 120, visible: true, nonHideable: true, type: 'string' },
      { field: 'bpDescription', headerText: 'Business Partner', visible: true, type: 'string' },
      { field: 'address', headerText: 'Address', visible: true, type: 'string' },
      { field: 'city', headerText: 'City', visible: true, type: 'string', filter: { type: 'Excel' } },
      { field: 'stateProvince', headerText: 'State/Province', visible: true, type: 'string', filter: { type: 'Excel' } },
      { field: 'zipCode', headerText: 'Zip Code', visible: true, type: 'string' },
      { field: 'serviceDepartment', headerText: 'Service Department', visible: true, type: 'string' },
      { field: 'telephone', headerText: 'Telephone', visible: true, type: 'string' },
      { field: 'country', headerText: 'Country', visible: true, type: 'string', filter: { type: 'Excel' } },
      { field: 'eMail', headerText: 'Email', visible: true, type: 'string' },
      { field: 'isNationalAccount', headerText: 'National Account', visible: false, showInColumnChooser: false, type: 'boolean', allowFiltering: false },
      { field: 'nationalAccount', headerText: 'National Account', visible: true, type: 'string', filter: { type: 'Menu' } },
      {
        field: 'Actions', headerText: 'Actions', showInColumnChooser: false, textAlign: 'Center', width: 120, allowFiltering: false, allowSorting: false,
        freeze: 'Right', allowReordering: false, commands: [
          {
            title: 'Proceed',
            buttonOption: {
              iconCss: `fas fa-arrow-right`,
              cssClass: `e-flat action-button`
            }
          }
        ]
      }
    ] : [
      { field: 'soldToBP', headerText: 'ID', width: 120, visible: true, nonHideable: true, type: 'string' },
      { field: 'bpDescription', headerText: 'Business Partner Description', visible: true, type: 'string' },
      { field: 'addressCode', headerText: 'Address Code', visible: true, type: 'string' }
    ]
    this.mainGrid = {
      columns,
      toolbar: ['Search',
        { text: '', id: 'clear-filter', align: 'Right', prefixIcon: 'fas fa-filter', cssClass: `grid-filter-icon`, tooltipText: 'Clear all Filters' }
      ],
      pageSettings: { pageSize: 50 }
    };
  }

  toolbarClick(args: ClickEventArgs, grid: GridComponent) {
    if (args.item.id === 'clear-filter') {
      handleClearFilter(grid);
    }
  }

  onGridCreated() {
    updateFilterIcon({ requestType: 'filtering' }, this.serviceOrderGrid);
    bindInfiniteScrollPrefetch(this.serviceOrderGrid, () => this.totalRecords, 0, (count) => { this.totalRecords = count; });
  }

  onDataBound() {
    bindInfiniteScrollPrefetch(this.serviceOrderGrid, () => this.totalRecords, 0, (count) => { this.totalRecords = count; });
  }

  onGridActionBegin(args, grid?) {
    gridActionBeginForFiltering(args, grid);
    if (args.requestType === 'infiniteScroll') setTimeout(() => this.serviceOrderGrid?.hideSpinner());
  }

  onGridActionComplete(args, grid) {
    if (args.requestType === 'infiniteScroll') setTimeout(() => grid?.hideSpinner());
    gridActionsForFiltering(args, grid);
    updateFilterIcon(args, grid);
    applyInfiniteGridTotalCount(grid, (count) => { this.totalRecords = count; }, args.requestType, args);
  }

  onCommandClick(args: any) {
    if (args.commandColumn.title === 'Proceed') {
      this.onClose.emit(args.rowData);
    }
  }

  close() {
    this.validationApplied = false;
    this.onClose.emit(null);
  }

  proceed() {
    this.onClose.emit(this.selectedRowData);
  }

  rowSelected(args) {
    this.selectedRowData = args.data;
  }

}
